/**
 * Comic-Style Copyright Warning Popup (Factory Pattern)
 * 
 * Enforces a "Comic Block" interaction on viewer entry.
 * Now supports Variable Configuration for "Popup Factory".
 * 
 * @param {HTMLElement} targetElement - The main content element to blur.
 * @param {Object} config - (Optional) Custom configuration from Factory.
 * @param {string} config.title - Override Title
 * @param {string} config.legalText - Override Legal HTML
 * @param {string} config.buttonText - Override Button Text
 * @param {string} config.theme - 'standard' | 'police' (davinci)
 */
export const CopyrightWarning = (targetElement, config = {}) => {
    // 1. Initial State: Block Content
    if (targetElement) {
        targetElement.style.filter = "blur(12px)";
        targetElement.style.transition = "filter 0.5s ease-out";
    }
    document.body.style.overflow = "hidden";

    // 2. Resolve Config (Factory Logic)
    // Default: Standard Webnovel Warning
    const DEFAULTS = {
        title: "꼼짝 마! 저작권 경찰!",
        icon: "👮‍♂️",
        legalText: `
            <p><strong>[저작권법 위반 경고]</strong></p>
            <p>
                본 작품은 저작권법의 보호를 받습니다. 무단 캡처, 복제, 유포 시 
                <strong>저작권법 제136조</strong>에 의거하여 
                <strong>5년 이하의 징역 또는 5천만 원 이하의 벌금</strong>에 처해질 수 있으며, 
                <strong>민/형사상 책임</strong>은 전적으로 사용자에게 있습니다.
            </p>
            <p class="text-red-600 font-bold mt-2 border-t border-black/10 pt-2">
                "불법 유포자는 IP 추적을 통해 끝까지 법적 책임을 묻습니다."
            </p>
        `,
        buttonText: "네, 착하게 감상할게요! 🥺",
        theme: 'standard'
    };

    const finalConfig = { ...DEFAULTS, ...config };

    // 3. Create Overlay
    // [CRITICAL] Z-Index: Max Integer (2147483647) to defeat all headers/toasts
    const overlay = document.createElement('div');
    overlay.className = "fixed inset-0 bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 animate-fade-in";
    overlay.style.zIndex = "2147483647"; // God Mode Z-Index
    overlay.id = "copyright-warning-overlay";

    // 4. Create Popup Card
    overlay.innerHTML = `
        <div class="bg-[#FDF6E3] border-4 border-black w-full max-w-md transform -rotate-1 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-8 relative flex flex-col items-center">
            
            <!-- Icon -->
            <div class="text-7xl mb-4 animate-bounce">
                ${finalConfig.icon}
            </div>

            <!-- Title -->
            <h2 class="text-3xl font-black text-center mb-6 tracking-tighter text-black break-keep" style="font-family: 'Black Han Sans', sans-serif;">
                ${finalConfig.title}
            </h2>

            <!-- Legal Body -->
            <div class="font-serif text-sm leading-relaxed text-zinc-900 space-y-3 mb-8 bg-white/50 p-4 border-2 border-black/10 rounded-lg text-justify break-keep">
                ${finalConfig.legalText}
            </div>

            <!-- Action Button -->
            <button id="btn-copyright-accept" class="w-full bg-red-500 border-4 border-black text-white font-bold text-xl py-4 hover:bg-red-400 hover:translate-y-1 hover:shadow-none transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-1 active:shadow-none rounded-lg">
                ${finalConfig.buttonText}
            </button>
        </div>
    `;

    // 5. Interaction Logic
    const btn = overlay.querySelector('#btn-copyright-accept');
    btn.onclick = () => {
        // Unlock
        if (targetElement) {
            targetElement.style.filter = "none";
        }
        document.body.style.overflow = "auto";

        // Remove Popup
        overlay.classList.add('opacity-0', 'pointer-events-none', 'transition-opacity', 'duration-300');
        setTimeout(() => {
            overlay.remove();
        }, 300);
    };

    // 6. Append to Body
    document.body.appendChild(overlay);
};
